import { IncidentId as SportIncidentId } from '@collector/sportsapi-client-legacy'
import { ActionQueue } from '@mobile/ActionQueue/ActionQueue'
import AddIncident from '@mobile/ActionQueue/Actions/Incidents/AddIncident'
import IncidentId from '@mobile/ActionQueue/Actions/Incidents/IncidentId'
import {
  isActionId,
  isIncidentId,
} from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'

export type DockedIncidentCorrelation = {
  actionId: string | null
  queueUuid: string | null
}

export type PendingVIPIncidentDock = {
  eventId: number
  incidentId: SportIncidentId
  participantId: number
  existingIncidentIds: Set<string>
  actionId: string | null
  queueUuid: string | null
}

export function correlateVIPDockWithAddAction(
  actionQueue: ActionQueue,
  incidentId: SportIncidentId,
  participantId: number,
): Pick<PendingVIPIncidentDock, 'actionId' | 'queueUuid'> {
  for (let index = actionQueue.queue.length - 1; index >= 0; index--) {
    const action = actionQueue.queue[index]
    if (!(action instanceof AddIncident)) {
      continue
    }

    const dto = action.incidentsQueuePostDTO
    if (
      dto.incident_id === incidentId &&
      dto.participant_team_id === participantId
    ) {
      return { actionId: action.id, queueUuid: action.uuid }
    }
  }

  return { actionId: null, queueUuid: null }
}

export function findPendingVIPDockIncident(
  incidents: ProbableLinkedIncident[],
  pending: PendingVIPIncidentDock,
): ProbableLinkedIncident | undefined {
  if (pending.actionId) {
    const byActionId = incidents.find(
      (linkedIncident) => linkedIncident.incident.id === pending.actionId,
    )
    if (byActionId) {
      return byActionId
    }
  }

  if (pending.queueUuid) {
    const byUuid = incidents.find(
      (linkedIncident) => linkedIncident.uuid === pending.queueUuid,
    )
    if (byUuid) {
      return byUuid
    }
  }

  const candidates = incidents.filter(
    (linkedIncident) =>
      !pending.existingIncidentIds.has(linkedIncident.incident.id) &&
      linkedIncident.incident.incident_id === pending.incidentId &&
      linkedIncident.participant?.id === pending.participantId,
  )

  if (candidates.length === 1) {
    return candidates[0]
  }

  return undefined
}

export function pickNewestServerIncident(
  candidates: ProbableLinkedIncident[],
): ProbableLinkedIncident {
  return candidates.reduce((newest, current) => {
    if (!isIncidentId(current.incident.id)) {
      return newest
    }
    if (!isIncidentId(newest.incident.id)) {
      return current
    }

    return IncidentId.fromPushMessageFormat(current.incident.id) >
      IncidentId.fromPushMessageFormat(newest.incident.id)
      ? current
      : newest
  })
}

function isMigratingFromQueueActionId(
  selected: ProbableLinkedIncident,
  correlation: DockedIncidentCorrelation | null,
): boolean {
  return (
    isActionId(selected.incident.id) ||
    !!correlation?.actionId ||
    !!correlation?.queueUuid
  )
}

/**
 * Re-resolves docked selection when WebSocket sync replaces a temporary queue
 * incident id (`collector:…`) with the server push-message id (`eventId-seq`).
 */
export function resolveSelectedIncidentAfterSync(
  incidents: ProbableLinkedIncident[],
  selected: ProbableLinkedIncident,
  correlation: DockedIncidentCorrelation | null = null,
): ProbableLinkedIncident | null {
  if (correlation?.queueUuid) {
    const byQueueUuid = incidents.find(
      (linkedIncident) => linkedIncident.uuid === correlation.queueUuid,
    )
    if (byQueueUuid) {
      return byQueueUuid
    }
  }

  if (correlation?.actionId) {
    const byActionId = incidents.find(
      (linkedIncident) => linkedIncident.incident.id === correlation.actionId,
    )
    if (byActionId) {
      return byActionId
    }
  }

  const byId = incidents.find(
    (linkedIncident) => linkedIncident.incident.id === selected.incident.id,
  )
  if (byId) {
    return byId
  }

  const candidates = incidents.filter(
    (linkedIncident) =>
      linkedIncident.incident.incident_id === selected.incident.incident_id &&
      linkedIncident.participant?.id === selected.participant?.id,
  )

  if (candidates.length === 0) {
    return null
  }

  if (candidates.length === 1) {
    return candidates[0]
  }

  const uuid = selected.uuid ?? correlation?.queueUuid ?? null
  if (uuid) {
    const byUuid = candidates.find(
      (linkedIncident) => linkedIncident.uuid === uuid,
    )
    if (byUuid) {
      return byUuid
    }
  }

  const serverCandidates = candidates.filter((linkedIncident) =>
    isIncidentId(linkedIncident.incident.id),
  )
  if (serverCandidates.length === 1) {
    return serverCandidates[0]
  }
  if (serverCandidates.length > 1) {
    if (isMigratingFromQueueActionId(selected, correlation)) {
      return pickNewestServerIncident(serverCandidates)
    }

    return null
  }

  const actionCandidates = candidates.filter((linkedIncident) =>
    isActionId(linkedIncident.incident.id),
  )
  if (actionCandidates.length === 1) {
    return actionCandidates[0]
  }

  return null
}

export function isDockedIncidentInFilteredList(
  filteredIncidents: ProbableLinkedIncident[],
  dockedIncidentId: string,
): boolean {
  return filteredIncidents.some(
    (linkedIncident) => linkedIncident.incident.id === dockedIncidentId,
  )
}

/**
 * When the docked incident is hidden by filters, inject it into the current page
 * so a placeholder row can anchor the floating dock overlay.
 */
export function injectFilteredOutDockedIncidentIntoPage(
  pageIncidents: ProbableLinkedIncident[],
  dockedIncident: ProbableLinkedIncident,
  reversePagination: boolean,
): ProbableLinkedIncident[] {
  if (
    pageIncidents.some(
      (linkedIncident) =>
        linkedIncident.incident.id === dockedIncident.incident.id,
    )
  ) {
    return pageIncidents
  }

  return reversePagination
    ? [...pageIncidents, dockedIncident]
    : [dockedIncident, ...pageIncidents]
}

export function getHistoryPageForVIPDock(
  reversePagination: boolean,
  totalIncidents: number,
  incidentsPerPage: number,
): number {
  if (totalIncidents === 0) {
    return 1
  }

  if (reversePagination) {
    return Math.ceil(totalIncidents / incidentsPerPage)
  }

  return 1
}

/**
 * Returns the pagination page that contains `dockedIncidentId` within
 * `filteredIncidents`, or `null` when the docked row is hidden by filters.
 */
export function getHistoryPageForDockedIncident(
  reversePagination: boolean,
  filteredIncidents: ProbableLinkedIncident[],
  incidentsPerPage: number,
  dockedIncidentId: string,
): number | null {
  const index = filteredIncidents.findIndex(
    (linkedIncident) => linkedIncident.incident.id === dockedIncidentId,
  )

  if (index === -1) {
    return null
  }

  if (incidentsPerPage <= 0) {
    return 1
  }

  if (!reversePagination) {
    return Math.floor(index / incidentsPerPage) + 1
  }

  const totalItems = filteredIncidents.length
  const remainingItems = totalItems % incidentsPerPage
  const firstPageSize =
    remainingItems || (totalItems > 0 ? incidentsPerPage : 0)

  if (index < firstPageSize) {
    return 1
  }

  return 2 + Math.floor((index - firstPageSize) / incidentsPerPage)
}
